'use client';

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HiOutlineDotsHorizontal, HiOutlineChevronDown } from 'react-icons/hi';

type ActionDropdownItem = {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'default' | 'primary' | 'success' | 'danger';
};

interface ActionDropdownProps {
  items: ActionDropdownItem[];
  buttonLabel?: string;
  align?: 'left' | 'right';
}

const toneClassName: Record<NonNullable<ActionDropdownItem['tone']>, string> = {
  default: 'text-slate-700 hover:bg-slate-100 hover:text-slate-900',
  primary: 'text-blue-700 hover:bg-blue-50 hover:text-blue-800',
  success: 'text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800',
  danger: 'text-red-600 hover:bg-red-50 hover:text-red-700',
};

const DROPDOWN_WIDTH = 190;
const VIEWPORT_GUTTER = 12;
const DROPDOWN_OFFSET = 8;
const FALLBACK_DROPDOWN_HEIGHT = 260;

export const ActionDropdown: React.FC<ActionDropdownProps> = ({
  items,
  buttonLabel = 'Actions',
  align = 'right',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (!buttonRef.current) return;

      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight =
        dropdownRef.current?.getBoundingClientRect().height || FALLBACK_DROPDOWN_HEIGHT;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const availableBelow = viewportHeight - rect.bottom - VIEWPORT_GUTTER;
      const availableAbove = rect.top - VIEWPORT_GUTTER;
      const shouldOpenUpward =
        availableBelow < Math.min(dropdownHeight, FALLBACK_DROPDOWN_HEIGHT) &&
        availableAbove > availableBelow;
      const left =
        align === 'right'
          ? rect.right - DROPDOWN_WIDTH
          : rect.left;
      const clampedLeft = Math.min(
        Math.max(VIEWPORT_GUTTER, left),
        Math.max(VIEWPORT_GUTTER, viewportWidth - DROPDOWN_WIDTH - VIEWPORT_GUTTER)
      );
      const maxHeight = Math.max(
        160,
        shouldOpenUpward ? availableAbove - DROPDOWN_OFFSET : availableBelow - DROPDOWN_OFFSET
      );

      setDropdownStyle({
        position: 'fixed',
        left: clampedLeft,
        ...(shouldOpenUpward
          ? { bottom: viewportHeight - rect.top + DROPDOWN_OFFSET }
          : { top: rect.bottom + DROPDOWN_OFFSET }),
        zIndex: 9999,
        minWidth: DROPDOWN_WIDTH,
        maxHeight,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    const handlePointerDown = (event: MouseEvent) => {
      const inButton = buttonRef.current?.contains(event.target as Node);
      const inDropdown = dropdownRef.current?.contains(event.target as Node);
      if (!inButton && !inDropdown) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, align]);

  const enabledItems = items.filter(Boolean);

  if (!enabledItems.length) {
    return null;
  }

  const dropdownPanel = (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_18px_50px_-18px_rgba(15,23,42,0.35)]"
      role="menu"
    >
      <div className="mb-1 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
        Quick Actions
      </div>
      <div className="space-y-1">
        {enabledItems.map((item) => (
          <button
            key={item.label}
            type="button"
            role="menuitem"
            disabled={item.disabled}
            onClick={() => {
              item.onClick();
              setIsOpen(false);
            }}
            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              toneClassName[item.tone || 'default']
            }`}
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-current/10">
              {item.icon}
            </span>
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="relative inline-flex">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:bg-slate-50 hover:shadow-md"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <HiOutlineDotsHorizontal size={15} />
        <span>{buttonLabel}</span>
        <HiOutlineChevronDown
          size={14}
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && typeof window !== 'undefined' && createPortal(dropdownPanel, document.body)}
    </div>
  );
};
